import _ from 'lodash';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import {
  Phase,
  readAllPhases
} from '../../../Helper/ApiHelper/KeyValueNetworkHelper';
import { readAllLiegenschaften } from '../../../Helper/ApiHelper/LiegenschaftenNetworkHelper';
import {
  CopyCatalogEP,
  CopyCatalogInfo,
  EpPosition,
  MinimizedEpCatalog,
  MinimizedLvCatalog,
  copyExternalEpCatalogVersionToPhase,
  generateEpTreeFromPositions,
  readAllEpCatalogsFromPlatform,
  readEpCatalogForPhase,
  readEpCatalogMetaForAllPhases,
  readLvCatalogMetaForPhase,
  readPossibleLvCatalogsForEpCatalog,
  removeAllHiddenCategoriesFromEpTree
} from '../../../Helper/ApiHelper/LvEpNetworkHelper';
import { DURATION_NOTIFICATION_SUCCESS_LONG } from '../../../Helper/Statics/Constants';
import BeeLoadingSpinner from '../../Atoms/BeeLoadingSpinner';
import EpLvLiegenschaftLV from '../../Molecules/LvEp/EpLvLiegenschaftLV';
import EpLvPhase from '../../Molecules/LvEp/EpLvPhase';
import './AlphaSetupTabLvVerwaltung.scss';
import { handlePromisesAndIgnoreErrors } from '../../../Helper/Util/PromiseHelper';
import { errorMsgToString } from '../../../Helper/ApiHelper/ErrorMessages';
import { EpCatalogPickerSelection } from '../../Molecules/Dialogs/AlphaEpCatalogPickerStep';

export function AlphaSetupTabLvVerwaltung() {
  const [dataIsLoading, setDataIsLoading] = useState<boolean>(false);
  const [liegenschaften, setLiegenschaften] = useState<any>([]);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [epLookup, setEpLookup] = useState<Map<string, any>>();
  const [allEpCatalogs, setAllEpCatalogs] = useState<CopyCatalogInfo[]>([]);
  const [lvOptionsByEpCatalogId, setLvOptionsByEpCatalogId] = useState<
    Map<string, Record<string, MinimizedLvCatalog[]>>
  >(new Map());
  //refs
  const toast = useRef<Toast>(null);

  ///////////
  // SETUP //
  ///////////
  useEffect(() => {
    setDataIsLoading(true);
    //load all liegenschaften

    readAllLiegenschaften()
      .then((ls: any) => {
        let liegenschaften = [...ls];
        setLiegenschaften(liegenschaften);
        readAllPhases()
          .then((phases: any) => {
            let phaseVals: Phase[] = [...phases];

            loadEpCatalogAndLVsAsync(phaseVals);
          })
          .catch(() => {
            //ERROR phasen lesen fehler
            setDataIsLoading(false);
          });
      })
      .catch(() => {
        //ERROR liegenschaften fehler
        setDataIsLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    readAllEpCatalogsFromPlatform()
      .then((catalogs) => {
        setAllEpCatalogs(catalogs);
      })
      .catch((err: unknown) => {
        toast.current?.show({
          severity: 'error',
          summary: 'EP-Kataloge konnten nicht geladen werden',
          detail: errorMsgToString(err)
        });
      });
  }, []);

  function loadEpCatalog(phases: Phase[]) {
    let epPromises: Promise<any>[] = [];
    phases.forEach((phase) => {
      epPromises.push(readEpCatalogForPhase(phase.id));
    });
    handlePromisesAndIgnoreErrors(epPromises).then((payload: any) => {
      let epCLookup = new Map<string, any>();
      if (payload && payload.fulfilled) {
        payload.fulfilled.forEach((c: any) => {
          if (c.value && c.value.data && c.value.info) {
            let currCatalog: any = generateEpTreeFromPositions(c.value.data);
            currCatalog = currCatalog ? currCatalog : [];
            /*   currCatalog = removeAllHiddenCategoriesFromEpTree(
              currCatalog,
              false
            ); */
            epCLookup.set(c.value.info.phaseId, currCatalog);
          }
        });
      }
      setEpLookup(epCLookup);
    });
  }

  function loadEpCatalogAndLVsAsync(phases: Phase[]) {
    setDataIsLoading(true);
    //clean phases
    phases.forEach((p) => {
      p.lvs = [];
      p.ep = undefined;
    });
    readEpCatalogMetaForAllPhases()
      .then((epCatalogs: any) => {
        let epCats: MinimizedEpCatalog[] = [...epCatalogs];
        //search for phaseVals entry
        epCats.forEach((epE) => {
          if (epE && epE.phaseId) {
            //search for phaseVals entry
            for (let i = 0; i < phases.length; i++) {
              if (phases[i].id === epE.phaseId) {
                phases[i].ep = epE;
                break;
              }
            }
          }
        });
        //für jede phase einen minimalen lv ziehen!!!
        //fixme raus?
        let promises: any = [];
        phases.forEach((p) => {
          promises.push(readLvCatalogMetaForPhase(p.id));
        });
        Promise.all(promises)
          .then((entries) => {
            if (entries) {
              entries.forEach((phArray) => {
                if (phArray && phArray.length > 0) {
                  const pId = phArray[0].phaseId;
                  for (let i = 0; i < phases.length; i++) {
                    if (phases[i].id === pId) {
                      phases[i].lvs = phArray;
                      break;
                    }
                  }
                }
              });
            }
            loadEpCatalog(phases);

            const uniqueEpCatalogIds = Array.from(
              new Set(
                phases
                  .map((p) => p?.ep?.catalogId)
                  .filter((id) => id != null) as string[]
              )
            );

            const possibleLvPromises = uniqueEpCatalogIds.map((epId) =>
              readPossibleLvCatalogsForEpCatalog(epId)
            );

            Promise.all(possibleLvPromises)
              .then((allPossibleLvSets) => {
                const map = new Map<
                  string,
                  Record<string, MinimizedLvCatalog[]>
                >();
                allPossibleLvSets.forEach((lvOptions, idx) => {
                  map.set(uniqueEpCatalogIds[idx], lvOptions);
                });
                setLvOptionsByEpCatalogId(map);
              })
              .catch((err: unknown) => {
                toast.current?.show({
                  severity: 'error',
                  summary: 'LV-Kataloge konnten nicht geladen werden',
                  detail: errorMsgToString(err)
                });
              })
              .finally(() => {
                //finally set all data
                setPhases(phases);
                setDataIsLoading(false);
              });
          })
          .catch(() => {
            //Error loading LVs-Catalog for Phases
            setDataIsLoading(false);
          });
      })
      .catch(() => {
        //Error loading EP-Catalog for Phases
        setDataIsLoading(false);
      });
  }

  function applyEpCatalogFromCopyResponse(ep: CopyCatalogEP, phaseId: string) {
    const meta = ep.info;
    applyEpCatalogTreeAndMeta(phaseId, meta, ep.data ?? []);
  }

  function applyEpCatalogTreeAndMeta(
    phaseId: string,
    meta: MinimizedEpCatalog,
    positions: EpPosition[] | null | undefined
  ) {
    setPhases((prev) =>
      prev.map((p) => (p.id === phaseId ? { ...p, ep: meta } : p))
    );
    let currCatalog: any = generateEpTreeFromPositions(positions ?? []);
    currCatalog = currCatalog ? currCatalog : [];
    setEpLookup((prev) => {
      const next = new Map(prev);
      next.set(phaseId, currCatalog);
      return next;
    });
  }

  function selectEpCatalog(
    selection: EpCatalogPickerSelection,
    phaseId: string
  ) {
    if (!phaseId) {
      return;
    }

    copyExternalEpCatalogVersionToPhase(phaseId, selection.epCatalogProjectId)
      .then((ep: CopyCatalogEP) => {
        toast.current?.show({
          severity: 'success',
          summary: 'EP-Katalog',
          detail: 'Der EP-Katalog wurde der Phase zugewiesen.'
        });
        applyEpCatalogFromCopyResponse(ep, phaseId);
      })
      .catch((err: unknown) => {
        toast.current?.show({
          severity: 'error',
          summary: 'Zuweisung fehlgeschlagen',
          detail: errorMsgToString(err)
        });
      });
  }

  ////////////////
  // UI METHODS //
  ////////////////

  function generatePhases() {
    const pay = phases;

    let data = [];
    data.push({ type: 'nonPhase', header: 'Liegenschaft' });
    data.push({ type: 'nonPhase', header: 'Adresse' });
    pay.forEach((entry) => data.push(entry));

    return data;
  }

  function generateLiegenschaftLvUploadTemplate(
    liegenschaftId: string,
    phaseId: string
  ) {
    const phase = _.find(phases, function (e: Phase) {
      return e.id === phaseId;
    });

    return (
      <EpLvLiegenschaftLV
        phase={phase}
        propertyId={liegenschaftId}
        possibleLvCatalogs={
          phase?.ep?.catalogId
            ? lvOptionsByEpCatalogId.get(phase.ep.catalogId)
            : {}
        }
        onAdaptData={(headline, message) => {
          toast.current?.show({
            severity: 'success',
            summary: headline,
            detail: message,
            sticky: false,
            closable: true,
            life: DURATION_NOTIFICATION_SUCCESS_LONG
          });
          loadEpCatalogAndLVsAsync(phases);
        }}
      />
    );
  }

  const bodyTemplate = (
    data: any,
    options: ColumnBodyOptions,
    phaseId: string
  ) => {
    if (options) {
      if (options.field === 'field_0') {
        return (
          <div className={'lv-table-liegenschaft'}>
            <div className={'lv-number'}>{data.nummer}</div>
            <div>{data.name}</div>
          </div>
        );
      } else if (options.field === 'field_1') {
        return (
          <div>
            <div>
              <span>{data.adresse ? data.adresse.strasse : null}</span>{' '}
              <span>{data.adresse ? data.adresse.hausnummer : null}</span>
              <div></div>
              <span>
                {data.adresse ? data.adresse.postleitzahl : null}
              </span>{' '}
              <span>{data.adresse ? data.adresse.stadt : null}</span>
            </div>
          </div>
        );
      } else {
        return generateLiegenschaftLvUploadTemplate(data.id, phaseId);
      }
    }
  };

  const headerTemplate = (data: any, number: any) => {
    if (data.type === 'nonPhase') {
      return data.header;
    } else {
      return (
        <EpLvPhase
          phase={data}
          epTree={epLookup?.get(data.id)}
          allEpCatalogs={allEpCatalogs}
          onSelectEpCatalog={(catalog: EpCatalogPickerSelection) => {
            selectEpCatalog(catalog, data.id);
          }}
          onAdaptData={(headline, message) => {
            toast.current?.show({
              severity: 'success',
              summary: headline,
              detail: message,
              sticky: false,
              closable: true,
              life: DURATION_NOTIFICATION_SUCCESS_LONG
            });
            loadEpCatalogAndLVsAsync(phases);
          }}
        />
      );
    }
  };

  const dynamicColumns = generatePhases().map((col: any, i) => {
    const phaseId = col && col.id ? col.id : null;
    return (
      <Column
        body={(data: any, options: ColumnBodyOptions) =>
          bodyTemplate(data, options, phaseId)
        }
        className={
          col.type === 'nonPhase'
            ? col.header.replace(' ', '_') + '-col lv-nonPhase'
            : col.title.replace(' ', '_') + '-col lv-phase'
        }
        style={{ width: phaseId ? '5em' : '10em' }}
        frozen={phaseId ? false : true}
        alignFrozen="left"
        header={headerTemplate(col, i)}
      />
    );
  });

  function injectDescription() {
    return (
      <div className={'mb-3'}>
        Bitte laden Sie in folgender Tabelle für jede Phase einen EP-Katalog
        hoch. Anschließend kann für jede Liegenschaft ein Leistungsverzeichnis
        durch Auswahl eines passenden LV-Katalogs hinterlegt werden. Ist eine
        Phase noch nicht abgeschlossen, können Leistungsverzeichnisse
        aktualisiert werden. Für bereits abgelaufene Phasen können die
        Leistungsverzeichnisse nicht mehr verändert werden.
      </div>
    );
  }

  return (
    <div className={'alpha-lv-verwaltung m-2 mt-4 ml-1 p-2'}>
      <div className={''}>{injectDescription()}</div>
      {!dataIsLoading ? (
        <div className={'lv-verwaltung-table'}>
          <DataTable
            value={liegenschaften}
            scrollable
            scrollDirection="horizontal"
            emptyMessage={'Keine Liegenschaften vorhanden'}
          >
            {dynamicColumns}
          </DataTable>
        </div>
      ) : (
        <BeeLoadingSpinner strokeWidth={'3'} type={'secondary'} />
      )}
      <Toast ref={toast} position={'top-right'} />
    </div>
  );
}
